import React from 'react';
import { Link } from 'react-router-dom';
import Moment from 'src/libraries/moment.library';
import {
  ATMButton,
  ATMDatePicker,
  ATMDropdown,
  ATMGrid,
  ATMHeader,
  ATMMenu,
  ATMTab,
  IORGDataTableColumn,
  IORGDataTableQueryState,
  ORGDataTable,
  ATMInput,
  ATMCheckbox,
} from 'shared-it-appmod-ui';
import Lang from 'src/libraries/language';
import {
  IFireDispatch,
  IFireDispatchFilters,
} from 'src/models/fire-dispatch.model';
import TableSearch from 'src/components/atoms/table/table-search/table-search.component';
import Tabs from 'src/components/atoms/tabs/tabs.component';
import DispatchStatus from 'src/components/atoms/fire-dispatch-status/fire-dispatch-status.component';
import FireDispatchEmailNotification from './fire-dispatch-email-notification/fire-dispatch-email-notification.component';
import styles from './fire-dispatch-list.module.scss';
// import FlightRequestListPopover from './fire-dispatch-list-popover/fire-dispatch-list-popover.component';

type IProp = {
  data: IFireDispatch[];
  filters: IFireDispatchFilters;
  handleFetch: (params: IORGDataTableQueryState) => void;
  handleDownload: (params: IORGDataTableQueryState) => void;
  total?: number;
  cancelled?: number;
  completed?: number;
  dispatched?: number;
  inFlight?: number;
  landed?: number;
  totalItems?: number;
  loading: boolean;
  downloading: boolean;
  dateRange: string;
  openDetailsModal: (dispatchId?: number | null) => void;
  handleDateRange: (data) => void;
};

const FlightRequestListView: React.FC<IProp> = ({
  data,
  filters,
  total = 0,
  cancelled = 0,
  completed = 0,
  dispatched = 0,
  inFlight = 0,
  landed = 0,
  totalItems = 0,
  loading,
  dateRange,
  handleFetch,
  handleDownload,
  downloading,
  openDetailsModal,
  handleDateRange,
}) => {
  let year = Moment().format('YYYY') as any;
  const yearFilter: string[] = ['All', year, --year, --year].map((value) =>
    value.toString()
  );

  const customColumns: IORGDataTableColumn<IFireDispatch>[] = [
    {
      title: Lang.LBL_DISPATCH_FLIGHT_INFO,
      index: 'dispatchId',
      sortable: true,
      render: (_, value) => (
        <>
          <div className={styles.textLimit}>
            <div>
              <ATMHeader
                as="h3"
                onClick={() => openDetailsModal(value.dispatchId)}
              >
                {`${Lang.LBL_DISPATCH_NUMBER}  ${value.dispatchId} `}
              </ATMHeader>
            </div>
            <br />
            <div>{`${Lang.LBL_TAIL_NO}: ${value.tailNumber}`}</div>
            <br />
            <div>{`${Lang.LBL_FLIGHT_HOURS} ${value.flightHrs ?? ''}`}</div>
          </div>
        </>
      ),
    },
    {
      title: Lang.LBL_DISPATCH_DATE_AND_TIME,
      index: 'dispatchDate',
      sortable: true,
      render: (_, value) => (
        <span>
          {Moment(value.dispatchDate).format('MM/DD/YYYY')}
          {` - ${value.dispatchTime?.replace(':', '')} hrs`}
        </span>
      ),
    },
    {
      title: Lang.LBL_LIFTOFF_AND_RETURN_TIME,
      index: 'returnTime',
      sortable: false,
      render: (_, value) => (
        <>
          <span>
            {Lang.LBL_L}: {value.liftoffTime?.replace(':', '')}
          </span>
          <br />
          <span>
            {Lang.LBL_R}: {value.returnTime?.replace(':', '')}
          </span>
        </>
      ),
    },
    {
      title: Lang.LBL_RESPONSE_TIME,
      index: 'responseTime',
      sortable: false,
    },
    {
      title: Lang.LBL_FIRE_INFO,
      index: 'location',
      sortable: false,
      render: (_, value) => (
        <>
          <span className={styles.bold}>
            {value.fireName ? value.fireName : '- No Fire Name -'}
          </span>
          <br />
          <span>{Lang.LBL_ACRES_BURNED} </span>{' '}
          <span className={styles.bold}>
            {value.burnedAcres ? value.burnedAcres : 'n/a'}
          </span>
        </>
      ),
    },
    {
      title: Lang.LBL_LOCATION,
      index: 'location',
      sortable: false,
    },
    {
      title: Lang.LBL_FIRE_AGENCY,
      index: 'fireAgency',
      sortable: true,
      render: (_, value) => <span>{value.fireAgency}</span>,
    },
    {
      title: Lang.LBL_NUMBER_OF_DIPS,
      index: 'dipCount',
      sortable: false,
      render: (_, value) => <span>{value.dipCount}</span>,
    },
    {
      title: Lang.LBL_GALLONS_DROPPED,
      index: 'waterGallons',
      sortable: false,
      render: (_, value) => <span>{value.waterGallons}</span>,
    },
    {
      title: Lang.LBL_STATUS_ACTIONS,
      index: 'status',
      width: '200px',
      headerProps: {
        className: 'text-right',
      },
      sortable: false,
      render: (_, value) => (
        <div className="table-actions right">
          <DispatchStatus dispatchStatus={value.status ?? undefined} />
          <ATMButton
            as={Link}
            to={`/dispatch/${value.dispatchId}/edit`}
            className={styles.actionIcon}
            basic
            icon="edit outline"
          />
          <FireDispatchEmailNotification data={value} />
        </div>
      ),
    },
  ];

  // for UT Coverage
  if (process.env.REACT_APP_SWITCH_TO_UNIT_TEST_MODE === 'true') {
    if (customColumns[0].render) {
      customColumns[0].render(true, {} as IFireDispatch, 1, {} as any);
    }
    if (customColumns[1].render) {
      customColumns[1].render(true, {} as IFireDispatch, 1, {} as any);
    }
    if (customColumns[2].render) {
      customColumns[2].render(true, {} as IFireDispatch, 1, {} as any);
    }
    if (customColumns[4].render) {
      customColumns[4].render(true, {} as IFireDispatch, 1, {} as any);
    }
    if (customColumns[6].render) {
      customColumns[6].render(true, {} as IFireDispatch, 1, {} as any);
    }
    if (customColumns[7].render) {
      customColumns[7].render(true, {} as IFireDispatch, 1, {} as any);
    }
    if (customColumns[8].render) {
      customColumns[8].render(true, {} as IFireDispatch, 1, {} as any);
    }
    if (customColumns[9].render) {
      customColumns[9].render(true, {} as IFireDispatch, 1, {} as any);
    }
  }

  const table = (
    <ORGDataTable
      celled={false}
      columns={customColumns}
      data={data}
      onChange={handleFetch}
      total={totalItems}
      loading={loading}
      addParams={['ticket', 'dispatch', 'dispatchDate', 'fireName', 'year']}
      filteredBy={(filterList) =>
        (filterList || []).map(
          (filter) =>
            Lang.REQUEST_TABLE_FILTER_LABEL[filter.name] ?? filter.name
        )
      }
      sortable
      history
      counter
    >
      {({ state, setState }) => {
        const rangeDisabled = state.year !== 'All' || !state.year;

        return {
          toolbars: [
            () => <TableSearch state={state} setState={setState} />,
            () => (
              <ATMDropdown
                selection
                value={state.year ?? yearFilter[1]}
                options={yearFilter?.map((yr) => ({
                  key: yr,
                  value: yr,
                  text: yr,
                }))}
                onChange={(_, val) => {
                  if (val.value !== 'All') {
                    handleDateRange(`01/01/${val.value} - 12/31/${val.value}`);
                    setState({
                      ...state,
                      page: 1,
                      year: val.value,
                      dispatchDate: [
                        `01/01/${val.value}`,
                        `12/31/${val.value}`,
                      ],
                    });
                  } else {
                    handleDateRange('Select');
                    setState({
                      ...state,
                      page: 1,
                      year: val.value,
                      dispatchDate: ['01/01/2015', '12/31/2100'],
                    });
                  }
                  return val.value;
                }}
              />
            ),
            () => (
              <ATMButton
                basic
                color="blue"
                icon="download"
                content={Lang.LBL_DOWLOAD}
                floated="right"
                loading={downloading}
                disabled={downloading || total === 0}
                onClick={() => handleDownload(state)}
              />
            ),
          ],
          filters: ({ values, setValue }) => {
            return (
              <ATMGrid stretched doubling stackable>
                <ATMGrid.Row columns="equal">
                  <ATMGrid.Column className={styles.datepicker}>
                    <ATMHeader as="h3" content={Lang.LBL_DISPATCH_DATE} />
                    <ATMDatePicker
                      type="range"
                      className={rangeDisabled ? styles.disabledPicker : ''}
                      value={values.dispatchDate?.map(
                        (dispatchDate) => new Date(dispatchDate)
                      )}
                      disabled={rangeDisabled}
                      placeholder={dateRange}
                      onChange={(_, val) => setValue('dispatchDate', val.value)}
                    />
                  </ATMGrid.Column>
                  <ATMGrid.Column>
                    <ATMHeader as="h3" />
                    <ATMCheckbox
                      label={Lang.LBL_BILLABLE}
                      checked={values.billable || false}
                      onChange={(_, val) => {
                        setValue('billable', val.checked || undefined);
                      }}
                    />
                  </ATMGrid.Column>
                  <ATMGrid.Column>
                    <ATMHeader as="h3" content={Lang.LBL_FIRE_NAME} />
                    <ATMInput
                      placeholder={Lang.LBL_FIRE_NAME}
                      value={values.fireName}
                      onChange={(_, val) => setValue('fireName', val.value)}
                    />
                  </ATMGrid.Column>
                  <ATMGrid.Column>
                    <ATMHeader as="h3" content={Lang.LBL_FIRE_AGENCY} />
                    <ATMDropdown
                      selection
                      clearable
                      value={values.fireAgency}
                      options={filters.fireAgencies?.map((fireAgency) => ({
                        key: fireAgency?.replace(' ', ''),
                        value: fireAgency,
                        text: fireAgency,
                      }))}
                      onChange={(_, val) => setValue('fireAgency', val.value)}
                    />
                  </ATMGrid.Column>
                  <ATMGrid.Column>
                    <ATMHeader as="h3" content={Lang.LBL_TAIL_NO} />
                    <ATMDropdown
                      selection
                      clearable
                      value={values.tailNumber}
                      options={filters.tailNumbers?.map((tail) => ({
                        key: tail?.replace(' ', ''),
                        value: tail,
                        text: tail,
                      }))}
                      onChange={(_, val) => setValue('tailNumber', val.value)}
                    />
                  </ATMGrid.Column>
                </ATMGrid.Row>
              </ATMGrid>
            );
          },
        };
      }}
    </ORGDataTable>
  );

  const panes = [
    {
      menuItem: (
        <ATMMenu.Item key="All">
          {Lang.formatString(Lang.LBL_ALL, total)}
        </ATMMenu.Item>
      ),
      render: () => <ATMTab.Pane attached={false}>{table}</ATMTab.Pane>,
    },
    {
      menuItem: (
        <ATMMenu.Item key="Dispatched">
          {Lang.formatString(Lang.LBL_STATUS_DISPATCHED, dispatched)}
        </ATMMenu.Item>
      ),
      render: () => <ATMTab.Pane attached={false}>{table}</ATMTab.Pane>,
    },
    {
      menuItem: (
        <ATMMenu.Item key="In-Flight">
          {Lang.formatString(Lang.LBL_STATUS_IN_FLIGHT, inFlight)}
        </ATMMenu.Item>
      ),
      render: () => <ATMTab.Pane attached={false}>{table}</ATMTab.Pane>,
    },
    {
      menuItem: (
        <ATMMenu.Item key="Landed">
          {Lang.formatString(Lang.LBL_STATUS_LANDED, landed)}
        </ATMMenu.Item>
      ),
      render: () => <ATMTab.Pane attached={false}>{table}</ATMTab.Pane>,
    },
    {
      menuItem: (
        <ATMMenu.Item key="Cancelled">
          {Lang.formatString(Lang.LBL_STATUS_CANCELLED, cancelled)}
        </ATMMenu.Item>
      ),
      render: () => <ATMTab.Pane attached={false}>{table}</ATMTab.Pane>,
    },
    {
      menuItem: (
        <ATMMenu.Item key="Completed">
          {Lang.formatString(Lang.LBL_STATUS_COMPLETED, completed)}
        </ATMMenu.Item>
      ),
      render: () => <ATMTab.Pane attached={false}>{table}</ATMTab.Pane>,
    },
  ];

  // for UT Coverage
  if (process.env.REACT_APP_SWITCH_TO_UNIT_TEST_MODE === 'true') {
    panes[0].render();
    panes[1].render();
    panes[2].render();
    panes[3].render();
    panes[4].render();
    panes[5].render();
  }

  return (
    <>
      <ATMGrid>
        <ATMGrid.Row columns="equal">
          <ATMGrid.Column>
            <ATMHeader as="h1" content={Lang.TTL_AVIATION_SERVICE_DISPATCH} />
          </ATMGrid.Column>
          <ATMGrid.Column>
            <ATMButton
              basic
              color="blue"
              icon="plus"
              content={Lang.LBL_ADD_FIRE_DISPATCH}
              floated="right"
              as={Link}
              to="/dispatch/new"
            />
          </ATMGrid.Column>
        </ATMGrid.Row>
        <ATMGrid.Row>
          <ATMGrid.Column>
            <div className={styles.proTabs}>
              <Tabs menu={{ pointing: true }} panes={panes} />
            </div>
          </ATMGrid.Column>
        </ATMGrid.Row>
      </ATMGrid>
    </>
  );
};

export default FlightRequestListView;
